import { useState, useEffect } from 'react';
import { Check, Sparkles, Loader2 } from 'lucide-react';
import { UploadedMethodType, useOnboardingStore } from '../../../store/onboardingStore';
import { axiosInstanceN8N } from '../../../services/instance';
import WarrantyAndFreeShipping from '../components/WarrantyAndFreeShipping';
import { PriceConfiguration, PriceConfigurationProps } from '../components/PriceConfiguration';
import { dictionary } from '../../../utils/dictionary';
import { useAuth } from '../../../context/AuthContext';
import { getUniqueAttributeNames } from '../../../utils/onboarding';

type ColorPalette = {
  id: string;
  name: string;
  primaryColor: string;
  secondaryColor: string;
};

const colorPalettes: ColorPalette[] = [
  {
    id: 'professional-blue',
    name: 'Professional Blue',
    primaryColor: 'bg-blue-500',
    secondaryColor: 'bg-blue-200',
  },
  {
    id: 'natural-green',
    name: 'Natural Green',
    primaryColor: 'bg-emerald-500',
    secondaryColor: 'bg-emerald-200',
  },
  {
    id: 'creative-purple',
    name: 'Creative Purple',
    primaryColor: 'bg-[#A855F7]',
    secondaryColor: 'bg-[#E9D5FF]',
  },
  {
    id: 'warm-sunset',
    name: 'Warm Sunset',
    primaryColor: 'bg-orange-500',
    secondaryColor: 'bg-orange-100',
  },
  {
    id: 'minimal-gray',
    name: 'Minimal Gray',
    primaryColor: 'bg-gray-600',
    secondaryColor: 'bg-gray-300',
  },
  {
    id: 'royal-gold',
    name: 'Royal Gold',
    primaryColor: 'bg-amber-700',
    secondaryColor: 'bg-amber-200',
  }
];

export function StepVisualStyle() {
  const { language } = useAuth();
  const { data, updateVisualStyle, updateProductData, updateAdditionalOffers } = useOnboardingStore();
  const { visualStyle, productData } = data;

  const [brandName, setBrandName] = useState(visualStyle.brandName || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [brandNameSuggestions, setBrandNameSuggestions] = useState<string[]>([]);
  const [currentSuggestionIndex, setCurrentSuggestionIndex] = useState(0);
  const [customWarrantyDays, setCustomWarrantyDays] = useState<string>(
    productData.customWarrantyDays || '30'
  );

  useEffect(() => {
    setCustomWarrantyDays(productData.customWarrantyDays || '30');
  }, [productData.warranty, productData.customWarrantyDays]);

  const fetchSuggestedBrandNames = async () => {
    try {
      const response = await axiosInstanceN8N.post('/webhook/suggested-brand-names', {
        product_name: (data.productData?.name || "").split(' ').slice(0, 5).join(' '),
        product_description: (data.productData?.details || "").split(' ').slice(0, 5).join(' ')

      });

      if (response.data.data && response.data.data.brand_names.length > 0) {
        return response.data.data.brand_names;
      }

      // Fallback names in case API response is empty
      return ["Brandify", "BrandMaster", "BrandWizard", "BrandNova", "BrandPeak"];
    } catch (error) {
      console.error("Error fetching brand names:", error);
      // Fallback names in case of error
      return ["Brandify", "BrandMaster", "BrandWizard", "BrandNova", "BrandPeak"];
    }
  };

  // Set the suggested brand name on initial load
  useEffect(() => {
    const loadBrandNames = async () => {
      setIsGenerating(true);

      // Fetch all brand name suggestions
      const suggestions = await fetchSuggestedBrandNames();
      setBrandNameSuggestions(suggestions);

      // If no brand name is set, use the first suggestion
      if (!visualStyle.brandName && suggestions.length > 0) {
        setBrandName(suggestions[0]);
        updateVisualStyle({
          brandName: suggestions[0]
        });
      }

      // If the current palette is 'custom', reset it to a default
      if (visualStyle.colorPalette === 'custom') {
        updateVisualStyle({
          colorPalette: 'professional-blue'
        });
      }

      setIsGenerating(false);
    };

    loadBrandNames();
  }, []);

  const handleSelectPalette = (paletteId: string) => {
    updateVisualStyle({
      colorPalette: paletteId as any
    });
  };

  const handleBrandNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newBrandName = e.target.value;
    setBrandName(newBrandName);
    updateVisualStyle({
      brandName: newBrandName
    });
  };

  const generateNewBrandName = () => {
    if (isGenerating || brandNameSuggestions.length === 0) return;

    setIsGenerating(true);

    try {
      // Move to the next suggestion in the array
      const nextIndex = (currentSuggestionIndex + 1) % brandNameSuggestions.length;
      const newName = brandNameSuggestions[nextIndex];

      setBrandName(newName);
      updateVisualStyle({
        brandName: newName
      });

      setCurrentSuggestionIndex(nextIndex);
    } catch (error) {
      console.error("Error cycling brand name:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleWarrantyToggle = (enabled: boolean) => {
    updateProductData({
      ...productData,
      warranty: enabled ? (customWarrantyDays ? 'custom' : '30days') : 'none',
      customWarrantyDays: enabled ? customWarrantyDays : undefined
    });
  };

  const handleCustomWarrantyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomWarrantyDays(value);

    if (value && !isNaN(Number(value))) {
      updateProductData({
        ...productData,
        warranty: 'custom',
        customWarrantyDays: value
      });
    }
  };

  const handleFreeShippingChange = (enabled: boolean) => {
    updateProductData({ freeShipping: enabled });
  };

  const handleMainProductPriceChange = (price: number) => {
    if (data.productData.uploadedMethod !== UploadedMethodType.PREVIOUS || price > 0) {
      updateProductData({ price: price.toString() });
    }
  }

  const handleBumpOfferPriceChange = (price: number) => {
    updateAdditionalOffers({
      ...data.additionalOffers,
      bumpOffer: {
        enabled: data.additionalOffers.bumpOffer?.enabled ?? false,
        productName: data.additionalOffers.bumpOffer?.productName ?? '',
        price: price.toString(),
        description: data.additionalOffers.bumpOffer?.description ?? '',
        image: data.additionalOffers.bumpOffer?.image ?? '',
        isManualEntry: data.additionalOffers.bumpOffer?.isManualEntry ?? false,
        productLink: data.additionalOffers.bumpOffer?.productLink ?? '',
      }
    });
  }

  const handleUpsellPriceChange = (id: string, price: number) => {
    updateAdditionalOffers({ ...data.additionalOffers, upsell: { ...data.additionalOffers.upsell, products: data.additionalOffers.upsell.products.map((product) => ({ ...product, price: price.toString() })) } });
  }

  const mainProductPrice = data.productData.uploadedMethod === UploadedMethodType.PREVIOUS ? Number(data.productData.price) || 0 : 0;
  const bumpOfferPrice = data.additionalOffers.bumpOffer?.uploadedMethod === UploadedMethodType.PREVIOUS ? Number(data.additionalOffers.bumpOffer?.price) || 0 : 0;

  const uniqueAttributeNames = getUniqueAttributeNames(data.productData.variants || []);

  const propsPriceConfiguration: PriceConfigurationProps = {
    mainProduct: {
      name: data.productData.name,
      price: mainProductPrice,
      imageUrl: data.visualContent.imagesBase64[0] || data.visualContent.images[0],
      uploadedMethod: data.productData.uploadedMethod,
      variantsAttributes: uniqueAttributeNames
    },
    onMainProductPriceChange: handleMainProductPriceChange,
    bumpOffer: {
      name: data.additionalOffers.bumpOffer?.productName,
      price: bumpOfferPrice,
      imageUrl: data.additionalOffers.bumpOffer?.url_images || data.additionalOffers.bumpOffer?.image || "",
      uploadedMethod: data.additionalOffers.bumpOffer?.uploadedMethod 
    },
    onBumpOfferPriceChange: handleBumpOfferPriceChange,
    upsells: data.additionalOffers.upsell.products.map((product) => ({
      id: product.id,
      name: product.productName,
      price: Number(product.price) || 0,
      imageUrl: product.url_images?.[0] || product.image || "",
      uploadedMethod: product.uploadedMethod,
    })),
    onUpsellPriceChange: handleUpsellPriceChange
  }

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-medium mb-4">{dictionary["one-last-thing-tell-us-about-your-brand"][language]}</h3>

      {/* Brand Name Section - Compact Layout */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1">
          <label htmlFor="brand-name" className="block text-sm font-medium text-gray-700 mb-1">
            {dictionary["brand-name"][language]}
          </label>
          {isGenerating && brandNameSuggestions.length === 0 ? (
            <div className="input flex items-center justify-between">
              <span className="text-gray-400">{dictionary["generating-suggestions-with-ai"][language]}</span>
              {/* <Loader2 className="w-4 h-4 animate-spin" /> */}
            </div>
          ) : (
            <input
              id="brand-name"
              type="text"
              className="input"
              placeholder={dictionary["enter-your-brand-name-placeholder"][language]}
              value={brandName}
              onChange={handleBrandNameChange}
              disabled={isGenerating && brandNameSuggestions.length === 0}
            />
          )}
        </div>
        <div className="pt-6">
          <button
            type="button"
            onClick={generateNewBrandName}
            disabled={isGenerating || brandNameSuggestions.length === 0}
            className="btn btn-secondary h-10 py-0"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {dictionary["generating"][language]}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                {dictionary["generate"][language]}
              </>
            )}
          </button>
        </div>
      </div>

      <PriceConfiguration
        mainProduct={propsPriceConfiguration.mainProduct}
        onMainProductPriceChange={propsPriceConfiguration.onMainProductPriceChange}
        bumpOffer={propsPriceConfiguration.bumpOffer}
        onBumpOfferPriceChange={propsPriceConfiguration.onBumpOfferPriceChange}
        upsells={propsPriceConfiguration.upsells}
        onUpsellPriceChange={propsPriceConfiguration.onUpsellPriceChange}
      />

      <WarrantyAndFreeShipping
        warrantyEnabled={data.productData?.warranty !== 'none'}
        handleWarrantyToggle={handleWarrantyToggle}
        customWarrantyDays={data.productData?.customWarrantyDays || '30'}
        handleCustomWarrantyChange={handleCustomWarrantyChange}
        freeShippingEnabled={data.productData?.freeShipping}
        handleFreeShippingToggle={handleFreeShippingChange}
      />
    </div>
  );
}